/**
 * Modal.tsx — reusable accessible modal (task K01).
 *
 * All overlay dialogs (setup/join game, manage/player wishlist, reveal)
 * render through this component so the a11y behavior is implemented once:
 *
 *  - ARIA dialog semantics: `role="dialog"` + `aria-modal="true"` +
 *    `aria-labelledby` pointing at the modal's <h2> title (the accessible
 *    name is always the title text);
 *  - focus moves INTO the dialog on open (first enabled focusable element,
 *    or the panel itself as a fallback target);
 *  - focus is TRAPPED: Tab/Shift+Tab wrap inside the panel (keyboard-only
 *    users cannot tab into the background page while a modal is open);
 *  - Escape closes the dialog when safe — `blocked` (e.g. a request is in
 *    flight) suppresses Escape and disables the close button;
 *  - focus RETURNS to the element that opened the dialog on close;
 *  - body scroll is locked while open (restored on close).
 *
 * The DOM structure and class names (`.overlay-backdrop` / `.overlay-panel`
 * / `.overlay-header` / `.overlay-close`) are the established ones, so the
 * existing styles and tests keep applying. Backdrop click deliberately does
 * NOT close (form data must not be lost by accident); use Escape, Cancel,
 * or the × button.
 */
import { useEffect, useId, useRef, type ReactNode } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

interface ModalProps {
  /** Dialog title — rendered as the <h2> and used for aria-labelledby. */
  title: string;
  /** Called by Escape (when not blocked), Cancel links, or the × button. */
  onClose: () => void;
  /**
   * Suppress Escape and disable the close button while true (e.g. while a
   * request is in flight — closing mid-request is the unsafe case).
   */
  blocked?: boolean;
  children: ReactNode;
}

export function Modal({ title, onClose, blocked = false, children }: ModalProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement | null>(null);

  // The window keydown listener must always see the latest handlers without
  // re-subscribing on every render.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const blockedRef = useRef(blocked);
  blockedRef.current = blocked;

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;

    const focusables = (): HTMLElement[] =>
      Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (el) => !el.hasAttribute('disabled'),
      );

    // Move initial focus into the dialog.
    (focusables()[0] ?? panel).focus();

    // Lock background scroll while the dialog is open.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        if (blockedRef.current) return; // closing now would be unsafe
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab') return;

      const list = focusables();
      if (list.length === 0) {
        // Nothing focusable inside: pin focus to the panel itself.
        event.preventDefault();
        panel.focus();
        return;
      }
      const firstEl = list[0];
      const lastEl = list[list.length - 1];
      const active = document.activeElement as HTMLElement | null;
      const inside = active !== null && panel.contains(active);
      if (event.shiftKey) {
        // Shift+Tab on the first element (or focus escaped outside): wrap to last.
        if (!inside || active === firstEl) {
          event.preventDefault();
          lastEl.focus();
        }
      } else if (!inside || active === lastEl) {
        // Tab on the last element (or focus escaped outside): wrap to first.
        event.preventDefault();
        firstEl.focus();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      // Return focus to the element that opened the dialog.
      if (previouslyFocused instanceof HTMLElement && previouslyFocused.isConnected) {
        previouslyFocused.focus();
      }
    };
    // Focus management is a per-MOUNT concern; handler freshness comes from
    // the refs above, so this effect must not re-run on prop changes.
  }, []);

  return (
    <div className="overlay-backdrop">
      <div
        className="overlay-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={panelRef}
        tabIndex={-1}
      >
        <header className="overlay-header">
          <h2 id={titleId}>{title}</h2>
          <button
            type="button"
            className="overlay-close"
            onClick={onClose}
            disabled={blocked}
            aria-label="Close"
          >
            ×
          </button>
        </header>
        {children}
      </div>
    </div>
  );
}
